import { createStyles, Text } from "@mantine/core";
import { GraphQL } from "@rpi/openapi-api";
import { tokens } from "@blurb/codex-react";
import React from "react";
import { CustomSelect, CustomSelectItemProps, CustomSelectWrapper } from "./SelectUtils";

const color = tokens.color.semantic;

type SelectApiCredentialType = Pick<GraphQL.ApiCredentialsResponse, "apiKey" | "description" | "sharedSecret">;

/** Figma "Menu" / "Menu Detail": a raised 4px card of 8px-radius rows. A row carrying a description
 *  is taller, so the two frames differ only in the row's vertical padding. */
const useStyles = createStyles(() => ({
  dropdown: {
    borderRadius: 4,
    backgroundColor: color.bg.surfaceRaised,
    boxShadow: "0px 4px 12px 1px rgba(0, 0, 0, 0.1)",
    // Doubled up to outrank the `p={0}` Mantine's SelectPopover hardcodes on its Popover.Dropdown,
    // which otherwise wins and leaves the rows flush against the card edge.
    "&&": {
      padding: 8,
    },
  },

  item: {
    display: "flex",
    flexDirection: "column",
    gap: 4,
    padding: 8,
    borderRadius: 8,
    backgroundColor: color.bg.neutral,
    "&[data-hovered]": {
      backgroundColor: color.bg.inputHover,
    },
  },

  itemWithDescription: {
    paddingTop: 12,
    paddingBottom: 12,
  },

  apiKey: {
    fontSize: 16,
    lineHeight: "24px",
    color: color.text.bold,
  },

  description: {
    fontSize: 14,
    lineHeight: 1.4,
    color: color.text.subtle,
  },
}));

const SelectApiCredentialItem = React.forwardRef<HTMLDivElement, CustomSelectItemProps<SelectApiCredentialType>>(
  ({ element, className, ...others }, ref) => {
    const { classes, cx } = useStyles();
    return (
      <div ref={ref} className={cx(className, { [classes.itemWithDescription]: !!element.description })} {...others}>
        <Text className={classes.apiKey} children={element.apiKey} />
        {element.description && <Text className={classes.description} children={element.description} />}
      </div>
    );
  }
);

export const SelectApiCredential: CustomSelectWrapper<SelectApiCredentialType> = ({ getValue, ...props }) => {
  const { classes } = useStyles();
  return (
    <CustomSelect
      label="API Credential"
      placeholder="Select API Credential"
      nothingFound="No Api Keys"
      maxDropdownHeight={200}
      classNames={{ dropdown: classes.dropdown, item: classes.item }}
      itemComponent={SelectApiCredentialItem}
      getItemProps={(apiCredential) => ({
        label: apiCredential.apiKey,
        value: getValue ? getValue(apiCredential) : apiCredential.apiKey,
        element: apiCredential,
      })}
      {...props}
    />
  );
};
